import { postUserAttribution, UserAttributionBody } from 'services/user';

import {
  Attribution,
  hasMarketingConsent,
  readAttribution,
} from './attribution';
import { createPersistedFlagStore } from './persistedFlagStore';

// Raw click IDs are only sent once the owner approved storing them (the backend clears them
// after 90 days). Flip this together with the backend column and the privacy text.
export const SEND_RAW_CLICK_IDS = false;

const CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;
const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid'] as const;
// Replies after which retrying would not change anything.
const FINISHED_REASONS = ['created', 'exists', 'empty'];
// Below this a timestamp is in seconds, not milliseconds (the backend wants milliseconds).
const SECONDS_LIMIT = 1e11;

const sentFlags = createPersistedFlagStore({
  storageKey: 'gumb_attribution_sent',
});
// One attempt per user and page load, so repeated auth checks can never loop.
const attempted = new Set<string>();

const firstSeenMs = (ts: unknown): number | null => {
  if (typeof ts !== 'number' || !Number.isFinite(ts) || ts <= 0) return null;

  return ts < SECONDS_LIMIT ? ts * 1000 : ts;
};

/** Maps the cookie to the request body: whitelist only, never e-mail, names or full URLs. */
export const buildAttributionPayload = (
  attr: Attribution,
  { includeRawClickIds }: { includeRawClickIds: boolean },
): UserAttributionBody => {
  const body: UserAttributionBody = {};

  CAMPAIGN_KEYS.forEach((key) => {
    if (attr[key]) body[key] = attr[key];
  });
  body.click_id_type = CLICK_ID_KEYS.find((key) => attr[key]) || 'none';
  if (attr.landing_path) body.landing_path = attr.landing_path;
  if (attr.referrer_host) body.referrer_host = attr.referrer_host;
  const seen = firstSeenMs(attr.ts);

  if (seen) body.first_seen_at = seen;
  if (includeRawClickIds) {
    CLICK_ID_KEYS.forEach((key) => {
      if (attr[key]) body[key] = attr[key];
    });
  }

  return body;
};

/**
 * Sends the first-touch cookie to the backend once per user. Fire and forget: never awaited by
 * the login, never throws. The "sent" flag is only set after the backend answered with
 * created, exists or empty; an error, a 400 or a network failure leaves it open for a later login.
 */
export const sendAttributionOnce = (userId?: string | null): void => {
  try {
    if (!userId || attempted.has(userId) || sentFlags.has(userId)) return;
    const attr = readAttribution();

    if (!attr || !hasMarketingConsent()) return;
    attempted.add(userId);
    postUserAttribution(
      buildAttributionPayload(attr, { includeRawClickIds: SEND_RAW_CLICK_IDS }),
    )
      .then((result) => {
        if (result && FINISHED_REASONS.includes(result.reason)) {
          sentFlags.set(userId);
        }
      })
      .catch(() => undefined);
  } catch {
    // attribution must never break the login
  }
};
